import React, { useRef, useState } from 'react';
import * as http from '../../../../utils/http';
import * as translator from '../../../../utils/translator';
import './PhotoUploadModal.css';

import { clientConfig } from '../../../../config';

const silhouetteFront = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-silhouette-front.png`;
const silhouetteSide = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-silhouette-side.png`;
const cameraIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-camera.svg`;
const galleryIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-gallery.svg`;
const spinnerIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-spinner.svg`;
const mirrorIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-mirror.svg`;
const fullIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-full.svg`;
const starsIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-stars.svg`;
const userIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-user.svg`;
const badgeOkIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-badge-ok.svg`;
const badgeFailIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-badge-fail.svg`;
const exampleGood = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-example-good.png`;
const exampleSelfie = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-example-selfie.png`;
const exampleBlur = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-example-blur.png`;

type SlotState = 'idle' | 'uploading' | 'done';

interface SlotData {
  state: SlotState;
  progress: number;
  previewUrl: string | null;
}

interface Props {
  hashID: string;
  countryID: string;
  language: string;
  isWaitingActions: boolean;
  initialFrontUrl?: string | null;
  initialSideUrl?: string | null;
  onClose: () => void;
  onBothUploaded: (frontUrl: string, sideUrl: string) => void;
}

const isMobile = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

const PhotoUploadModal: React.FC<Props> = ({
  hashID,
  countryID,
  language,
  isWaitingActions,
  initialFrontUrl = null,
  initialSideUrl = null,
  onClose,
  onBothUploaded,
}) => {
  const t = (key: string) => translator.translate(language, 'my_account', `PhotoUploadModal/${key}`);
  const [front, setFront] = useState<SlotData>(() =>
    initialFrontUrl
      ? { state: 'done', progress: 100, previewUrl: initialFrontUrl }
      : { state: 'idle', progress: 0, previewUrl: null }
  );
  const [side, setSide] = useState<SlotData>(() =>
    initialSideUrl
      ? { state: 'done', progress: 100, previewUrl: initialSideUrl }
      : { state: 'idle', progress: 0, previewUrl: null }
  );
  const [visible, setVisible] = useState(true);
  const [pendingFiles, setPendingFiles] = useState<{ front: File | null; side: File | null }>({ front: null, side: null });
  const [submitError, setSubmitError] = useState(false);

  const frontGalleryRef = useRef<HTMLInputElement>(null);
  const sideGalleryRef = useRef<HTMLInputElement>(null);

  const canSubmit = front.state === 'done' && side.state === 'done';

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const uploadFile = async (file: File, slot: 'front' | 'side'): Promise<boolean> => {
    const setter = slot === 'front' ? setFront : setSide;
    const previewUrl = URL.createObjectURL(file);

    setter({ state: 'uploading', progress: 0, previewUrl });

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('type', slot);

    try {
      await http.postToServer(
        `${countryID}/account/orders/${hashID}/photo-upload`,
        formData,
        [http.MIDDLEWARES.CUSTOMER_ID],
        {
          onUploadProgress: (event: ProgressEvent) => {
            if (event.total) {
              const pct = Math.round((event.loaded / event.total) * 100);
              setter((prev) => ({ ...prev, progress: pct }));
            }
          },
        }
      );
      setter((prev) => ({ ...prev, state: 'done', progress: 100 }));
      return true;
    } catch {
      setter({ state: 'idle', progress: 0, previewUrl: null });
      return false;
    }
  };

  // For non-waiting orders: stage the file client-side, upload only on submit
  const handleFile = (file: File, slot: 'front' | 'side') => {
    setSubmitError(false);
    if (!isWaitingActions) {
      const previewUrl = URL.createObjectURL(file);
      const setter = slot === 'front' ? setFront : setSide;
      setter({ state: 'done', progress: 100, previewUrl });
      setPendingFiles(prev => ({ ...prev, [slot]: file }));
    } else {
      uploadFile(file, slot);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, slot: 'front' | 'side') => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFile(file, slot);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(false);
    try {
      if (!isWaitingActions) {
        if (pendingFiles.front) {
          const ok = await uploadFile(pendingFiles.front, 'front');
          if (!ok) return;
        }
        if (pendingFiles.side) {
          const ok = await uploadFile(pendingFiles.side, 'side');
          if (!ok) return;
        }
      }
      await http.postToServer(
        `${countryID}/account/orders/${hashID}/submit-photos`,
        {},
        [http.MIDDLEWARES.CUSTOMER_ID]
      );
    } catch {
      // Don't report success for a rejected submit: the order stays gated and gets
      // auto-cancelled on day 5 while the customer sees "completed".
      setSubmitError(true);
      return;
    }
    onBothUploaded(front.previewUrl!, side.previewUrl!);
    handleClose();
  };

  const handleEdit = (slot: 'front' | 'side') => {
    setSubmitError(false);
    const setter = slot === 'front' ? setFront : setSide;
    setter({ state: 'idle', progress: 0, previewUrl: null });
    if (!isWaitingActions) {
      setPendingFiles(prev => ({ ...prev, [slot]: null }));
    }
  };

  const handleCameraClick = (slot: 'front' | 'side') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.setAttribute('capture', 'environment');
    Object.assign(input.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      pointerEvents: 'none',
    });

    const cleanup = () => {
      if (document.body.contains(input)) document.body.removeChild(input);
    };

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) handleFile(file, slot);
      cleanup();
    });

    document.body.appendChild(input);
    input.click();

    // Cleanup if user cancels the picker without selecting
    input.addEventListener('cancel', cleanup);
    setTimeout(cleanup, 120_000);
  };

  const renderSlot = (slot: 'front' | 'side', data: SlotData) => {
    const galleryRef = slot === 'front' ? frontGalleryRef : sideGalleryRef;
    const label = slot === 'front' ? t('upload_front_label') : t('upload_side_label');
    const placeholderText = slot === 'front' ? t('front_placeholder') : t('side_placeholder');

    return (
      <div key={slot} className="pum-slot">
        <p className="pum-slot-label">{label}</p>

        {data.state === 'idle' && (
          <>
            <div className="pum-slot-placeholder">
              <svg className="pum-slot-placeholder-icon" xmlns="http://www.w3.org/2000/svg" width="29" height="24" viewBox="0 0 29 24" fill="none">
                <path d="M18.3637 1.37654C17.9505 1.1301 17.4784 0.999993 16.9973 1H11.6729C11.1923 0.999974 10.7206 1.1298 10.3077 1.37574C9.89483 1.62168 9.5561 1.97461 9.32733 2.39719L8.67528 3.60241C8.44652 4.02499 8.10779 4.37792 7.69491 4.62386C7.28204 4.8698 6.81036 4.99963 6.32976 4.9996H3.66688C2.95958 4.9996 2.28125 5.28052 1.78111 5.78057C1.28097 6.28062 1 6.95883 1 7.666V19.6648C1 20.372 1.28097 21.0502 1.78111 21.5502C2.28125 22.0503 2.95958 22.3312 3.66688 22.3312H25.0019C25.7092 22.3312 26.3876 22.0503 26.8877 21.5502C27.3878 21.0502 27.6688 20.372 27.6688 19.6648V7.666C27.6688 6.95883 27.3878 6.28062 26.8877 5.78057C26.3876 5.28052 25.7092 4.9996 25.0019 4.9996H22.339C21.8579 4.99961 21.3858 4.8695 20.9726 4.62306C20.5595 4.37661 20.2207 4.02302 19.9922 3.59974L19.3441 2.39986C19.1156 1.97658 18.7768 1.62299 18.3637 1.37654Z" stroke="#616975" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="14.33" cy="13.67" r="4.5" stroke="#616975" strokeWidth="2"/>
              </svg>
              <p className="pum-slot-placeholder-text">{placeholderText}</p>
            </div>
            <div className="pum-slot-buttons">
              {isMobile && (
                <button className="pum-slot-btn" onClick={() => handleCameraClick(slot)}>
                  <div className="pum-slot-btn-icon">
                    <img src={cameraIcon} alt="" />
                  </div>
                  <span>{t('camera_btn')}</span>
                </button>
              )}
              <button className="pum-slot-btn" onClick={() => galleryRef.current?.click()}>
                <div className="pum-slot-btn-icon">
                  <img src={galleryIcon} alt="" />
                </div>
                <span>{t('gallery_btn')}</span>
              </button>
            </div>
          </>
        )}

        {data.state === 'uploading' && (
          <div className="pum-slot-uploading">
            {data.previewUrl && (
              <img src={data.previewUrl} alt="" className="pum-uploading-preview" />
            )}
            <div className="pum-uploading-overlay">
              <img src={spinnerIcon} alt="" className="pum-spinner" />
              <div className="pum-uploading-info">
                <p className="pum-uploading-text">{t('uploading_text')}</p>
                <div className="pum-uploading-progress">
                  <div className="pum-progress-track">
                    <div className="pum-progress-fill" style={{ width: `${data.progress}%` }} />
                  </div>
                  <p className="pum-uploading-pct">{data.progress}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {data.state === 'done' && (
          <>
            <div className="pum-done-preview">
              {data.previewUrl
                ? <img src={data.previewUrl} alt={slot === 'front' ? t('done_label_front') : t('done_label_side')} className="pum-done-img" />
                : <svg className="pum-done-check-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="#269c6e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
              }
            </div>
            <p className="pum-done-label">{slot === 'front' ? t('done_label_front') : t('done_label_side')}</p>
            <button className="pum-edit-btn" onClick={() => handleEdit(slot)}>{t('edit_btn')}</button>
          </>
        )}

        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => handleFileChange(e, slot)}
        />
      </div>
    );
  };

  return (
    <div
      className={`pum-overlay${visible ? ' pum-overlay-visible' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`pum-sheet${visible ? ' pum-sheet-visible' : ''}`}>
        <div className="pum-header">
          <div className="pum-breadcrumb">
            <span className="pum-step">{t('step_label')}</span>
            <span className="pum-breadcrumb-sep"> / </span>
            <span className="pum-breadcrumb-section">{t('section_label')}</span>
          </div>
          <button className="pum-close-btn" onClick={handleClose} aria-label="Close">✕</button>
        </div>

        <div className="pum-body">
          {/* Card 1: Eligibility */}
          <div className="pum-section-card pum-info-card">
            <div className="pum-info-text">
              <p className="pum-info-title">{t('info_title')}</p>
              <p className="pum-info-desc">
                {t('info_desc')}
              </p>
            </div>
            <div className="pum-silhouettes-row">
              <div className="pum-silhouette-card">
                <div className="pum-silhouette-img-wrap">
                  <img src={silhouetteFront} alt="Front view silhouette" className="pum-silhouette-img" />
                </div>
                <p className="pum-silhouette-label">{t('front_view_label')}</p>
              </div>
              <div className="pum-silhouette-card">
                <div className="pum-silhouette-img-wrap">
                  <img src={silhouetteSide} alt="Side view silhouette" className="pum-silhouette-img" />
                </div>
                <p className="pum-silhouette-label">{t('side_view_label')}</p>
              </div>
            </div>
          </div>

          {/* Upload slots */}
          <div className="pum-slots">
            {renderSlot('front', front)}
            {renderSlot('side', side)}
          </div>

          {/* Submit */}
          <button
            className={`pum-submit-btn${canSubmit ? ' pum-submit-btn-active' : ' pum-submit-btn-disabled'}`}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {t('submit_btn')}
          </button>

          {submitError && (
            <p className="pum-submit-error" role="alert">{t('submit_failed')}</p>
          )}

          {/* Card 2: Recommendations */}
          <div className="pum-section-card pum-rec-card">
            <p className="pum-rec-title">{t('rec_title')}</p>
            <div className="pum-rec-grid">
              <div className="pum-rec-row">
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon-lg">
                    <img src={mirrorIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('rec_mirror')}</span>
                </div>
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon">
                    <img src={fullIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('rec_full')}</span>
                </div>
              </div>
              <div className="pum-rec-row">
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon-lg">
                    <img src={starsIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('rec_lit')}</span>
                </div>
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon">
                    <img src={userIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('rec_solo')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Do's & Don'ts */}
          <div className="pum-section-card pum-dos-card">
            <p className="pum-dos-title">{t('dos_title')}</p>
            <div className="pum-examples">
              <div className="pum-example">
                <div className="pum-example-photo pum-example-photo-light">
                  <img src={exampleGood} alt="" className="pum-example-img" />
                  <div className="pum-example-badge pum-example-badge-ok">
                    <img src={badgeOkIcon} alt="" />
                  </div>
                </div>
                <p className="pum-example-label">{t('dos_full_body')}</p>
              </div>
              <div className="pum-example">
                <div className="pum-example-photo pum-example-photo-gray">
                  <img src={exampleSelfie} alt="" className="pum-example-img" />
                  <div className="pum-example-badge pum-example-badge-fail">
                    <img src={badgeFailIcon} alt="" />
                  </div>
                </div>
                <p className="pum-example-label">{t('dos_no_selfie')}</p>
              </div>
              <div className="pum-example">
                <div className="pum-example-photo pum-example-photo-gray">
                  <img src={exampleBlur} alt="" className="pum-example-img pum-example-img-blur" />
                  <div className="pum-example-badge pum-example-badge-fail">
                    <img src={badgeFailIcon} alt="" />
                  </div>
                </div>
                <p className="pum-example-label">{t('dos_blur')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;
